import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import SelectableEnrollmentTable from "./components/SelectableEnrollmentTable";

interface BulkAddDemandModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

const BulkAddDemandModal: React.FC<BulkAddDemandModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const { enrollments, selectedIds, selectedEnrollmentIds, toggle, toggleAll, allSelected } =
    useEnrollmentSelection(selectedEnquiryIds, enquiryEnrollmentsMap, studentList, show);

  const [heads, setHeads] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingHeads, setLoadingHeads] = useState(false);
  const [installmentHeadId, setInstallmentHeadId] = useState<number | "">("");
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingHeads(true);
    setError("");
    EnrollmentService.getDemandHeads()
      .then((resp) => {
        if (cancelled) return;
        setHeads(resp.data?.data?.heads || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load demands");
      })
      .finally(() => {
        if (!cancelled) setLoadingHeads(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  const handleClose = () => {
    setInstallmentHeadId("");
    setAmount("");
    setRemarks("");
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!installmentHeadId) {
      setError("Select a demand.");
      return;
    }
    if (!(Number(amount) > 0)) {
      setError("Amount must be greater than 0.");
      return;
    }
    if (selectedEnrollmentIds.length === 0) {
      setError("Select at least one enrollment.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkAddDemand({
        enrollmentIds: selectedEnrollmentIds,
        installmentHeadId: Number(installmentHeadId),
        amount: Number(amount),
        remarks: remarks.trim() || null,
      });
      setSummary(resp.data?.data?.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to add demand");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {summary ? "Add Demand — Results" : "Add Demand"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {summary ? (
          <div
            style={{
              display: "flex",
              gap: 12,
              fontSize: 13,
              color: "var(--text-strong)",
            }}
          >
            <span>
              <strong>{summary.total ?? 0}</strong> processed
            </span>
            <span style={{ color: "var(--status-success-text)" }}>
              <strong>{summary.added ?? 0}</strong> added
            </span>
            <span style={{ color: "var(--status-info-text)" }}>
              <strong>{summary.skipped ?? 0}</strong> skipped
            </span>
            <span style={{ color: "var(--status-danger)" }}>
              <strong>{summary.failed ?? 0}</strong> failed
            </span>
          </div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                Add Demand <span style={{ color: "var(--status-danger)" }}>*</span>
              </Form.Label>
              <Form.Select
                size="sm"
                value={installmentHeadId}
                onChange={(e) => setInstallmentHeadId(e.target.value ? Number(e.target.value) : "")}
                disabled={loadingHeads}
              >
                <option value="">-- Select Demand--</option>
                {heads.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                Amount <span style={{ color: "var(--status-danger)" }}>*</span>
              </Form.Label>
              <Form.Control
                type="number"
                size="sm"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Amount"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Remarks"
              />
            </Form.Group>

            <SelectableEnrollmentTable
              enrollments={enrollments}
              selectedIds={selectedIds}
              allSelected={allSelected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              disabled={submitting}
              caption={`Enrollments (${selectedEnrollmentIds.length} of ${enrollments.length} selected) — the demand is added to each`}
            />
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {summary ? (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                loadingHeads ||
                !installmentHeadId ||
                selectedEnrollmentIds.length === 0
              }
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Submitting…
                </>
              ) : (
                <>Submit</>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkAddDemandModal;
